/**
 * The session as a file: every frame in and out, every timed event with its latencies, and the
 * conversation they produced. A tester reporting something can hand over one artifact instead of
 * screenshots of the drawer, and it is the same data the drawer renders rather than a summary of it.
 */

import { resolveEndpoint } from './config';
import { buildTurns, type DebugState, type DebugTurn } from './debug';
import { parseModelId, type ModelSelection } from './models';
import type { FrameLogEntry, Message } from './store';
import type { DeviceMode, SignInDetails, VoiceMode } from '@/protocol/frames';

/** The parts of the session a trace is built from. */
export interface TraceSource {
  endpoint: string;
  deviceMode: DeviceMode | null;
  voiceMode: VoiceMode;
  sessionId: string | null;
  signIn: SignInDetails;
  modelId: string;
  messages: Message[];
  debug: DebugState;
  frames: FrameLogEntry[];
  framesDropped: number;
  audioFramesSent: number;
  unknownFrameTypes: string[];
  unknownPayloadTypes: string[];
}

export interface Trace {
  /** Names the shape so a reader can tell one of these from any other JSON a tester attaches. */
  format: 'voice-console-trace';
  version: 1;
  exportedAt: string;
  session: {
    /** The endpoint as typed, which may be a same-origin path. */
    endpoint: string;
    /** What that resolved to, since a path alone does not say which host answered. */
    resolvedEndpoint: string;
    deviceMode: DeviceMode | null;
    voiceMode: VoiceMode;
    sessionId: string | null;
    signInDetails: SignInDetails;
    /**
     * The provider and model this run was signed in on — what the numbers below belong to — or null
     * when the handshake named none and the deployment answered on its own default.
     */
    model: ModelSelection | null;
    /**
     * Client clock minus server clock, in ms. Every server stamp in this file is already
     * normalized onto the client clock; this is recorded so a reader can undo that.
     */
    clockOffsetMs: number | null;
  };
  counts: {
    turns: number;
    framesLogged: number;
    /** Frames the log dropped to stay bounded. Non-zero means this trace does not start at the top. */
    framesDropped: number;
    /** Microphone frames sent, which are counted rather than logged. */
    audioFramesSent: number;
  };
  /** Frame types and payload types the server sent that this client has no handler for. */
  unhandled: {
    frameTypes: string[];
    payloadTypes: string[];
  };
  conversation: Message[];
  turns: DebugTurn[];
  frames: FrameLogEntry[];
}

/**
 * Builds the trace for a session.
 *
 * @param source the session state to capture
 * @param now the moment of export
 * @returns the trace, ready to serialize
 */
export function buildTrace(source: TraceSource, now: Date): Trace {
  const turns = buildTurns(source.debug);

  return {
    format: 'voice-console-trace',
    version: 1,
    exportedAt: now.toISOString(),
    session: {
      endpoint: source.endpoint,
      resolvedEndpoint: resolveEndpoint(source.endpoint),
      deviceMode: source.deviceMode,
      voiceMode: source.voiceMode,
      sessionId: source.sessionId,
      signInDetails: source.signIn,
      model: parseModelId(source.modelId),
      clockOffsetMs: source.debug.clockOffset,
    },
    counts: {
      turns: turns.length,
      framesLogged: source.frames.length,
      framesDropped: source.framesDropped,
      audioFramesSent: source.audioFramesSent,
    },
    unhandled: {
      frameTypes: source.unknownFrameTypes,
      payloadTypes: source.unknownPayloadTypes,
    },
    conversation: source.messages,
    turns,
    frames: source.frames,
  };
}

/**
 * Serializes a trace for a file, indented because it is read by people at least as often as by
 * anything else.
 *
 * @param trace the trace to serialize
 * @returns its JSON
 */
export function serializeTrace(trace: Trace): string {
  return JSON.stringify(trace, null, 2);
}

/**
 * The filename a trace is saved under: sortable, and with no colons, which some filesystems refuse.
 *
 * @param now the moment of export
 * @returns the filename
 */
export function traceFilename(now: Date): string {
  const stamp = now.toISOString().slice(0, 19).replace('T', '-').replaceAll(':', '');
  return `voice-console-trace-${stamp}.json`;
}
